<template>
  <section class="multi-select">
    <header class="multi-header">
      <strong>{{ title }}</strong>
      <span>{{ normalizedValue.length }} выбрано</span>
    </header>

    <div v-if="normalizedValue.length" class="selected-items">
      <div v-for="id in normalizedValue" :key="id" class="selected-item" :class="{ inactive: optionById(id)?.inactive }">
        <div class="selected-text">
          <strong>{{ optionById(id)?.label || `${unknownLabel} ${id}` }}</strong>
          <small>{{ optionById(id)?.details || `ID: ${id} · значение отсутствует в справочнике` }}</small>
        </div>
        <button type="button" :aria-label="`Удалить ${id}`" @click="remove(id)">×</button>
      </div>
    </div>
    <div v-else class="empty">{{ emptyText }}</div>

    <input v-model="query" class="search" :placeholder="placeholder">
    <div v-if="loading" class="empty">Загрузка…</div>
    <div v-else-if="error" class="warning">{{ errorText }}</div>
    <div v-else class="options">
      <label class="select-all" :class="{ disabled: filteredOptions.length === 0 }">
        <input
          type="checkbox"
          :checked="allFilteredSelected"
          :indeterminate="someFilteredSelected"
          :disabled="filteredOptions.length === 0"
          @change="toggleFiltered"
        >
        <span>
          {{ query.trim() ? 'Выбрать всех найденных' : 'Выбрать всех' }}
          <small>{{ selectedFilteredCount }} из {{ filteredOptions.length }}</small>
        </span>
      </label>
      <label v-for="option in filteredOptions" :key="option.id" class="option" :class="{ inactive: option.inactive }">
        <input
          type="checkbox"
          :checked="selectedSet.has(Number(option.id))"
          @change="toggleOne(Number(option.id), $event.target.checked)"
        >
        <span class="option-main">
          <strong>{{ option.label }}</strong>
          <small>{{ option.details }}</small>
        </span>
        <span v-if="option.status" class="status">{{ option.status }}</span>
      </label>
      <div v-if="filteredOptions.length === 0" class="empty">Ничего не найдено</div>
    </div>
  </section>
</template>

<script>
import { computed, ref, watch } from 'vue'
import {
  filterOptions,
  normalizeSelection,
  selectionState,
  toggleSelection,
} from './groupMultiSelectUtils.mjs'

export default {
  name: 'GroupMultiSelect',
  props: {
    modelValue: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    title: { type: String, required: true },
    placeholder: { type: String, default: 'Поиск' },
    emptyText: { type: String, default: 'Ничего не выбрано' },
    unknownLabel: { type: String, default: 'ID' },
    loading: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    errorText: { type: String, default: 'Не удалось загрузить справочник. Ранее выбранные значения будут сохранены.' },
    serverSearch: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'search'],
  setup(props, { emit }) {
    const query = ref('')
    const normalizedValue = computed(() => normalizeSelection(props.modelValue))
    const selectedSet = computed(() => new Set(normalizedValue.value))
    const optionsById = computed(() => new Map(props.options.map((option) => [Number(option.id), option])))
    const optionById = (id) => optionsById.value.get(Number(id))
    const filteredOptions = computed(() => {
      if (props.serverSearch) return props.options
      return filterOptions(props.options, query.value)
    })
    const filteredState = computed(() => selectionState(normalizedValue.value, filteredOptions.value.map((option) => option.id)))
    const selectedFilteredCount = computed(() => filteredState.value.count)
    const allFilteredSelected = computed(() => filteredState.value.all)
    const someFilteredSelected = computed(() => filteredState.value.some)
    const update = (values) => emit('update:modelValue', normalizeSelection(values))
    const remove = (id) => update(normalizedValue.value.filter((value) => value !== Number(id)))
    const toggleOne = (id, checked) => {
      update(toggleSelection(normalizedValue.value, [id], checked))
    }
    const toggleFiltered = (event) => {
      update(toggleSelection(
        normalizedValue.value,
        filteredOptions.value.map((option) => option.id),
        event.target.checked,
      ))
    }
    let searchTimer = null
    watch(query, (val) => {
      if (!props.serverSearch) return
      if (searchTimer) clearTimeout(searchTimer)
      searchTimer = setTimeout(() => emit('search', (val || '').trim()), 300)
    })
    return {
      query, normalizedValue, selectedSet, optionById, filteredOptions,
      selectedFilteredCount, allFilteredSelected, someFilteredSelected,
      remove, toggleOne, toggleFiltered,
    }
  },
}
</script>

<style scoped>
.multi-select { border: 1px solid #d1d5db; border-radius: 10px; min-width: 0; overflow: hidden; }
.multi-header { align-items: center; background: #f8fafc; display: flex; justify-content: space-between; padding: 9px 12px; }
.multi-header strong { color: #1f2937; font-size: 13px; }
.multi-header span { color: #64748b; font-size: 10px; }
.selected-items { border-top: 1px solid #e5e7eb; display: flex; flex-wrap: wrap; gap: 6px; max-height: 112px; overflow-y: auto; padding: 8px; }
.selected-item { align-items: center; background: #eef4ff; border: 1px solid #dbeafe; border-radius: 8px; display: flex; gap: 6px; max-width: 100%; padding: 5px 7px; }
.selected-item.inactive, .option.inactive { opacity: 0.65; }
.selected-text { display: flex; flex-direction: column; min-width: 0; }
.selected-text strong, .option-main strong { color: #1f2937; font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.selected-text small, .option-main small { color: #6b7280; font-size: 9px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.selected-item button { align-items: center; background: transparent; border: 0; color: #64748b; cursor: pointer; display: inline-flex; font-size: 17px; height: 22px; justify-content: center; padding: 0; width: 22px; }
.search { border: 0; border-bottom: 1px solid #e5e7eb; border-top: 1px solid #e5e7eb; box-sizing: border-box; font-size: 12px; outline: none; padding: 9px 11px; width: 100%; }
.options { max-height: 260px; overflow-y: auto; padding: 4px; }
.select-all { align-items: center; background: #f8fafc; border-bottom: 1px solid #e5e7eb; cursor: pointer; display: flex; gap: 8px; margin: -4px -4px 4px; padding: 8px 10px; position: sticky; top: -4px; z-index: 1; }
.select-all span { color: #1f2937; display: flex; flex: 1; font-size: 11px; font-weight: 700; justify-content: space-between; }
.select-all small { color: #64748b; font-size: 9px; font-weight: 500; }
.select-all.disabled { cursor: default; opacity: 0.6; }
.option { align-items: center; border-radius: 7px; cursor: pointer; display: flex; gap: 8px; padding: 7px; }
.option:hover { background: #f1f5f9; }
.option-main { display: flex; flex: 1; flex-direction: column; min-width: 0; }
.status { background: #f1f5f9; border-radius: 999px; color: #64748b; font-size: 9px; padding: 2px 6px; }
.empty, .warning { color: #6b7280; font-size: 11px; padding: 10px; text-align: center; }
.warning { background: #fff7ed; color: #9a3412; }
</style>
